import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { Edit, Eye } from "lucide-react";
import { EmailTemplateModal } from "./EmailTemplateModal";
import { EmailPreviewModal } from "./EmailPreviewModal";

interface EmailTemplate {
  id: string;
  template_key: string;
  template_name: string;
  category: string;
  is_active: boolean;
  variables: any;
  subject_it: string;
  subject_en: string;
  subject_es: string;
  body_html_it: string;
  body_html_en: string;
  body_html_es: string;
  body_text_it: string | null;
  body_text_en: string | null;
  body_text_es: string | null;
}

export function EmailTemplateTable() {
  const { t, i18n } = useTranslation();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  useEffect(() => {
    console.log('[i18n] Testing key:', t("admin.settings.email.templates.title"));
    console.log('[i18n] All translations loaded:', i18n.hasLoadedNamespace('translation'));
  }, [t, i18n]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("email_templates")
        .select("*")
        .order("category", { ascending: true })
        .order("template_key", { ascending: true });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error("Error loading templates:", error);
      toast({
        title: t("admin.settings.toast.error") || "Errore",
        description: t("admin.settings.email.templates.messages.loadError") || "Errore caricamento template",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [t]);

  const handleToggleActive = async (template: EmailTemplate) => {
    try {
      const { error } = await supabase
        .from("email_templates")
        .update({ is_active: !template.is_active })
        .eq("id", template.id);

      if (error) throw error;

      setTemplates(templates.map(t => 
        t.id === template.id ? { ...t, is_active: !t.is_active } : t
      ));

      toast({
        title: t("admin.settings.toast.success") || "Successo",
        description: t("admin.settings.email.templates.messages.saveSuccess") || "Template salvato",
      });
    } catch (error) {
      console.error("Error toggling template:", error);
      toast({
        title: t("admin.settings.toast.error") || "Errore",
        description: t("admin.settings.email.templates.messages.saveError") || "Errore salvataggio",
        variant: "error",
      });
    }
  };

  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setModalOpen(true);
  };

  const handlePreview = (template: EmailTemplate) => {
    setPreviewTemplate(template);
    setPreviewModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedTemplate(null);
    loadTemplates();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <>
      <BrutalCard className="p-6">
        <div className="mb-4">
          <h3 className="text-xl font-bold mb-2">
            {t("admin.settings.email.templates.title") || "Template Email"}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("admin.settings.email.templates.description") || "Gestisci i template delle email di notifica"}
          </p>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="border-2 border-dark p-3 font-bold">
                  {t("admin.settings.email.templates.table.name") || "Nome"}
                </TableHead>
                <TableHead className="border-2 border-dark p-3 font-bold">
                  {t("admin.settings.email.templates.table.templateKey") || "Chiave"}
                </TableHead>
                <TableHead className="border-2 border-dark p-3 font-bold">
                  {t("admin.settings.email.templates.table.category") || "Categoria"}
                </TableHead>
                <TableHead className="border-2 border-dark p-3 text-center font-bold">
                  {t("admin.settings.email.templates.table.status") || "Stato"}
                </TableHead>
                <TableHead className="border-2 border-dark p-3 text-center font-bold">
                  {t("admin.settings.email.templates.table.actions") || "Azioni"}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id} className="hover:bg-surface/50 transition-colors">
                  <TableCell className="border-2 border-dark p-3 font-bold">
                    {template.template_name}
                  </TableCell>
                  <TableCell className="border-2 border-dark p-3 font-mono text-sm">
                    {template.template_key}
                  </TableCell>
                  <TableCell className="border-2 border-dark p-3">
                    <BrutalBadge variant="secondary">
                      {t(`admin.settings.email.templates.categories.${template.category}`) || template.category.toUpperCase()}
                    </BrutalBadge>
                  </TableCell>
                  <TableCell className="border-2 border-dark p-3 text-center">
                    <Switch
                      checked={template.is_active}
                      onCheckedChange={() => handleToggleActive(template)}
                    />
                  </TableCell>
                  <TableCell className="border-2 border-dark p-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handlePreview(template)}
                              className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                              aria-label={t("admin.settings.email.templates.actions.preview") || "Anteprima template"}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t("admin.settings.email.templates.actions.previewTooltip") || "Anteprima email"}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handleEdit(template)}
                              className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                              aria-label={t("admin.settings.email.templates.actions.edit") || "Modifica template"}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t("admin.settings.email.templates.actions.editTooltip") || "Modifica template email"}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </BrutalCard>

      {selectedTemplate && (
        <EmailTemplateModal
          template={selectedTemplate}
          open={modalOpen}
          onClose={handleModalClose}
        />
      )}

      {previewTemplate && (
        <EmailPreviewModal
          template={previewTemplate}
          open={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewTemplate(null);
          }}
        />
      )}
    </>
  );
}
